import React, { useState } from 'react'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { TableColumn } from 'react-data-table-component'
import { ZocksDataTable } from '../../../components/DataTable/ZocksDataTable'
import { useDocumentTitle } from '../../../common/useDocumentTitle'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { DEFAULT_PAGE_SIZE } from '../../../common/constants'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { MultiScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { useGetEmailTypesQuery, useUpdateEmailTypeMutation } from '../../../store/integrations/emailTypes'
import { Button } from '../../../components/Button/Button'
import { TagElement } from '../../../components/Tag/Tag'
import { useNavigate } from 'react-router-dom'
import { useZocksAuth, useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { UninstallEmailTypeModal } from './UninstallEmailTypeModal'
import { EmailType } from '../../../models/EmailType'
import { EmailTypeKindIcon } from './EmailTypeView'
import { Upsell } from '../../Plan/DeltaTrial/Upsell/Upsell'
import { DELTA_TRIAL_FEATURES } from '../../Plan/DeltaTrial/DeltaTrialFeatures'

export const EmailTypesTab = () => {
  const { isFetching: quotasFetching, hasQuota, quotaSuccess } = useZocksQuota()

  if (!quotaSuccess && quotasFetching) {
    return null
  }

  const emailEnabled = hasQuota('integration.emailEnabled.value')

  if (!emailEnabled) {
    return <Upsell feature={DELTA_TRIAL_FEATURES['inbox']} />
  }

  return <EmailTypesTabView />
}

export const EmailTypesTabView: React.FC = () => {
  const { tracked } = useTracking()
  const navigate = useNavigate()
  const { hasScope } = useZocksAuth()
  const hasDeleteRole = hasScope('**', 'email_type:delete', ['email_type:admin'])
  const hasUpdateRole = hasScope('**', 'email_type:update', ['email_type:admin'])

  useDocumentTitle('Setup')

  const accountId = useAppSelector(selectAccountId)

  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(1)

  const { data: emailTypes, isFetching } = useGetEmailTypesQuery(
    { accountId, pageRequest: { page: page - 1, size: perPage }, includeInactive: true },
    { refetchOnMountOrArgChange: true },
  )
  const [updateEmailType, { isLoading: updateIsLoading }] = useUpdateEmailTypeMutation()

  const [selectedEmailType, setSelectedEmailType] = useState<EmailType | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const { hasQuota } = useZocksQuota()
  const emailEnabled = hasQuota('integration.emailEnabled.value')

  const columns: TableColumn<EmailType>[] = [
    {
      name: 'Email Type',
      selector: (row) => row.name,
      sortable: true,
      grow: 12,
      cell: (row) => (
        <div className='d-flex gap-3 align-items-center mw-0'>
          <EmailTypeKindIcon emailType={row} />
          <div className='d-flex flex-column zocks-gap-0.25 mw-0'>
            <div className='button-link-medium text-decoration-none'>{row.name}</div>
            <div className='paragraph-small text-truncate'>{row.detectionConfig.description}</div>
          </div>
        </div>
      ),
    },
    {
      name: 'Tags',
      wrap: true,
      grow: 2,
      sortable: true,
      cell: (row) => <div className='d-flex gap-2 flex-wrap'>{row.systemEmailTypeId ? <TagElement>System</TagElement> : null}</div>,
    },
    {
      grow: 0,
      name: 'Active',
      sortable: true,
      button: true,
      selector: (row) => row.isActive,
      cell: (row) => {
        return (
          <>
            <div className='d-flex align-items-center'>
              <Form.Switch checked={row.isActive} onChange={() => handleActivateEmailType(row)} disabled={updateIsLoading || !hasUpdateRole} />
            </div>
          </>
        )
      },
    },
    {
      grow: 4,
      right: true,
      cell: (row) => {
        return (
          <div className='d-flex gap-3 align-items-center flex-grow-1 ps-3'>
            {!row.systemEmailTypeId ? (
              <div className='d-flex align-items-center gap-2'>
                <Button className='p-2' variant='primaryGhost' onClick={() => handleDeleteEmailType(row)} disabled={!hasDeleteRole}>
                  <i className='bi bi-trash' />
                </Button>
              </div>
            ) : null}
            <Button className='p-2' variant='primaryGhost' onClick={() => handleShowEmailType(row)}>
              <i className='bi bi-gear' />
            </Button>
            <Button className='p-2' variant='primaryGhost' onClick={() => handleCopyEmailType(row)} disabled={!hasUpdateRole}>
              <i className='bi bi-copy'></i>
            </Button>
          </div>
        )
      },
    },
  ]

  const _handleActivateEmailType = (emailType: EmailType) => {
    const { isActive, id: emailTypeId } = emailType
    updateEmailType({ accountId, emailTypeId, payload: { isActive: !isActive } })
  }

  const handleActivateEmailType = tracked(
    _handleActivateEmailType,
    {},
    {
      eventNameExtractor: (emailType: EmailType) => {
        const { isActive } = emailType
        return isActive ? TrackEvent.EMAIL_TYPE_DEACTIVATED : TrackEvent.EMAIL_TYPE_ACTIVATED
      },
      eventDetailsExtractor: (emailType: EmailType) => {
        const { id: emailTypeId } = emailType
        return {
          emailTypeId,
        }
      },
    },
  )

  const handleDeleteModalHide = () => {
    setSelectedEmailType(null)
    setShowDeleteModal(false)
  }

  const handleDeleteEmailType = (emailType: EmailType) => {
    setSelectedEmailType(emailType)
    setShowDeleteModal(true)
  }

  const handleShowEmailType = (emailType: EmailType) => {
    navigate(`/workspace/setup/email-type/email-type/details/${emailType.id}`)
  }

  const handleAddNewEmailType = () => {
    navigate(`/workspace/setup/email-type/email-type/create`)
  }

  const handleCopyEmailType = (emailType: EmailType) => {
    navigate(`/workspace/setup/email-type/email-type/copy/${emailType.id}`)
  }

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPage(page)
    setPerPage(newPerPage)
  }

  return (
    <Container fluid>
      {showDeleteModal && selectedEmailType ? (
        <UninstallEmailTypeModal show={showDeleteModal} onHide={handleDeleteModalHide} emailType={selectedEmailType} />
      ) : null}
      <Row className='mb-4'>
        <Col>
          <MultiScopedBlock
            scopedBlocks={[
              {
                scope: {
                  scope: 'email_type:create',
                  superScope: ['email_type:admin'],
                },
                missingScopeAction: MissingScopeAction.DISABLE,
              },
              {
                quota: { quota: 'integration.emailEnabled.value' },
                missingScopeAction: MissingScopeAction.UPGRADE,
              },
            ]}
          >
            <Button variant='primary' onClick={handleAddNewEmailType}>
              <i className='bi bi-plus' />
              Add new email type
            </Button>
          </MultiScopedBlock>
        </Col>
      </Row>
      <Row>
        <Col className='p-0'>
          <ZocksDataTable
            disabled={!emailEnabled}
            columns={columns}
            data={emailTypes?.data ?? []}
            progressPending={isFetching}
            pagination
            paginationServer
            paginationTotalRows={emailTypes?.page.totalElements}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
          />
        </Col>
      </Row>
    </Container>
  )
}
